/**
 * v1.6 MeterValues — wire builder + sampled-value synthesis.
 *
 * Session 12 / CR 2026-05-27 §4.1 — `buildSampledValues` and the SIM_*
 * constants used to live in `state/useStationStore.ts`. They are pure
 * wire-format builders, so they belong with the other v16 action
 * helpers. The state-aware wrapper `readMeasurandSet` stays in the
 * store (it reads the live `MeterValuesSampledData` CSL from
 * `useConfigurationStore`); this file exposes only the constants it
 * needs.
 */
import type { MeterValuesDomain } from '@/protocol/OcppAdapter';

export function buildMeterValuesRequest(req: MeterValuesDomain): Record<string, unknown> {
  const wire: Record<string, unknown> = {
    connectorId: req.connectorId,
    meterValue: req.meterValue.map((mv) => ({
      timestamp: mv.timestamp,
      sampledValue: mv.sampledValue.map((s) => ({ ...s })),
    })),
  };
  if (req.transactionId !== undefined) wire['transactionId'] = req.transactionId;
  return wire;
}

/**
 * BUG-020 — supported MeterValuesSampledData measurands. Anything in
 * the CSL outside this set is ignored (we don't model it).
 */
export const DEFAULT_SAMPLED_DATA =
  'Energy.Active.Import.Register,Power.Active.Import,SoC,Voltage,Current.Import';

export const KNOWN_MEASURANDS: ReadonlySet<string> = new Set<string>([
  'Energy.Active.Import.Register',
  'Power.Active.Import',
  'SoC',
  'Voltage',
  'Current.Import',
]);

/**
 * BUG-020 — sim conventions for synthesised Voltage / Current.Import:
 *   - AC: 230 V per phase, 3 phases ⇒ I = P / (230 × 3).
 *   - DC: 400 V single-string (typical CCS Combo) ⇒ I = P / 400.
 * Good-enough approximations for QA testing.
 */
const SIM_AC_VOLTAGE = 230;
const SIM_AC_PHASES = 3;
const SIM_DC_VOLTAGE = 400;

export interface BuildSampledValuesArgs {
  readonly measurandSet: ReadonlySet<string>;
  readonly connectorType: 'AC' | 'DC';
  readonly effectivePowerW: number;
  /** BUG-023 — cumulative energy register (Wh). */
  readonly accumulatedEnergyWh: number;
  readonly currentSocPct: number | null;
}

export interface SampledValue {
  readonly value: string;
  readonly measurand?: string;
  readonly unit?: string;
  readonly context?: string;
  readonly format?: string;
  readonly location?: string;
  readonly phase?: string;
}

export function buildSampledValues(args: BuildSampledValuesArgs): SampledValue[] {
  const out: SampledValue[] = [];
  const want = (m: string): boolean => args.measurandSet.has(m);

  if (want('Energy.Active.Import.Register')) {
    const energyWh = Math.round(args.accumulatedEnergyWh);
    out.push({
      value: String(energyWh),
      measurand: 'Energy.Active.Import.Register',
      unit: 'Wh',
      context: 'Sample.Periodic',
      format: 'Raw',
      location: 'Outlet',
    });
  }
  if (want('Power.Active.Import')) {
    out.push({
      value: String(args.effectivePowerW),
      measurand: 'Power.Active.Import',
      unit: 'W',
      context: 'Sample.Periodic',
      format: 'Raw',
      location: 'Outlet',
    });
  }
  // FEAT-011 — SoC is reported whenever a vehicle is bound (currentSocPct
  // non-null), on AC and DC alike. The connectorType still drives the
  // Voltage / Current.Import maths below, just not SoC visibility.
  if (want('SoC') && args.currentSocPct !== null) {
    out.push({
      value: args.currentSocPct.toFixed(1),
      measurand: 'SoC',
      unit: 'Percent',
      context: 'Sample.Periodic',
      format: 'Raw',
      location: 'EV',
    });
  }
  if (want('Voltage')) {
    const v = args.connectorType === 'AC' ? SIM_AC_VOLTAGE : SIM_DC_VOLTAGE;
    out.push({
      value: String(v),
      measurand: 'Voltage',
      unit: 'V',
      context: 'Sample.Periodic',
      format: 'Raw',
      location: 'Outlet',
    });
  }
  if (want('Current.Import')) {
    const ampsRaw =
      args.connectorType === 'AC'
        ? args.effectivePowerW / (SIM_AC_VOLTAGE * SIM_AC_PHASES)
        : args.effectivePowerW / SIM_DC_VOLTAGE;
    out.push({
      value: ampsRaw.toFixed(1),
      measurand: 'Current.Import',
      unit: 'A',
      context: 'Sample.Periodic',
      format: 'Raw',
      location: 'Outlet',
    });
  }
  return out;
}
